import axios from "axios"
import { supabase } from "../supabase.ts" // Import the supabase client

const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
})

// Add a request interceptor to include the user ID in the headers
api.interceptors.request.use(
  async (config) => {
    // Get the current session
    const {
      data: { session },
    } = await supabase.auth.getSession()

    // If there's a session, add the user ID to the headers
    if (session) {
      config.headers["user-id"] = session.user.id
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export interface PhoneNumber {
  phone_number: string
  phone_number_pretty: string
  phone_number_type?: "retell-twilio" | "retell-telnyx" | "custom"
  inbound_agent_id: string | null
  outbound_agent_id: string | null
  area_code: number
  nickname: string | null
  inbound_webhook_url: string | null
  last_modification_timestamp: number
}

export interface CreatePhoneNumberParams {
  inbound_agent_id?: string | null
  outbound_agent_id?: string | null
  area_code?: number
  nickname?: string
  inbound_webhook_url?: string | null
  userId?: string | null // Add userId here
}

export interface UpdatePhoneNumberParams {
  inbound_agent_id?: string | null
  outbound_agent_id?: string | null
  nickname?: string | null
  inbound_webhook_url?: string | null
}

// Add this interface after the other interfaces
export interface ImportPhoneNumberParams {
  phone_number: string
  termination_uri: string
  sip_trunk_auth_username?: string
  sip_trunk_auth_password?: string
  inbound_agent_id?: string | null
  outbound_agent_id?: string | null
  /** Must be a boolean literal when set (API rejects string "true"/"false"). */
  ignore_e164_validation?: boolean
  nickname?: string
  inbound_webhook_url?: string | null
  inbound_agents?: Array<{ agent_id: string; weight: number; agent_version?: number | string }>
  outbound_agents?: Array<{ agent_id: string; weight: number; agent_version?: number | string }>
  allowed_inbound_country_list?: string[] | null
  allowed_outbound_country_list?: string[] | null
  transport?: "TLS" | "TCP" | "UDP" | null
}

export const phoneNumberService = {
  list: async () => {
    const response = await api.get<PhoneNumber[]>("/phone-numbers")
    return response.data
  },

  get: async (phoneNumber: string) => {
    const response = await api.get<PhoneNumber>(`/phone-numbers/${phoneNumber}`)
    return response.data
  },

  create: async (params: CreatePhoneNumberParams) => {
    const response = await api.post<PhoneNumber>("/phone-numbers", params)
    return response.data
  },

  update: async (phoneNumber: string, params: UpdatePhoneNumberParams) => {
    const response = await api.patch<PhoneNumber>(`/phone-numbers/${phoneNumber}`, params)
    return response.data
  },

  delete: async (phoneNumber: string) => {
    await api.delete(`/phone-numbers/${phoneNumber}`)
  },

  // Add this method to the phoneNumberService object
  importPhoneNumber: async (params: ImportPhoneNumberParams) => {
    const response = await api.post<PhoneNumber>("/import-phone-number", params)
    return response.data
  },
}

